import { createSlice } from '@reduxjs/toolkit';
import { apiContainer } from '@vlabs/api-bindings';
import { getWsEventsQS, getEventsQS } from '@vlabs/api-bindings/src/luna-client/events/mappers';
import { roles } from '@vlabs/shared/config';
import UIfx from 'uifx';

import { selectAccountRole } from '@vlabs/pages/auth/selectors';
import { selectSourceOptionsByAccount } from '@vlabs/pages/sources/selectors';

let notify;

const store = createSlice({
  name: 'latestEvents',
  initialState: {
    data: [],
    notifyEvents: [],
    viewedNotifyIds: [],
    shownNotifyEvents: [],
    filters: {},
    filterCounts: 0,
    pageSize: 30,
    pageSizeOptions: [30],
    allIds: [],
    isSoundOn: false,
    soundThreshold: 50,
    isTopMatchHiding: false,
  },
  reducers: {
    setEvents(state, { payload: { events } }) {
      state.data = events;
      state.allIds = events?.map(({ event_id }) => event_id);
    },
    setParams(state, { payload: { filters, isSoundOn, soundThreshold } = {} }) {
      state.filters = filters;
      if (isSoundOn !== undefined) state.isSoundOn = isSoundOn;
      if (soundThreshold !== undefined) state.soundThreshold = soundThreshold;
    },
    setFilterCounts(state, { payload: count = 0 }) {
      state.filterCounts = count;
    },
    setNotifyEvents(state, { payload: { events } }) {
      const viewedNotifyIds = [...state.viewedNotifyIds];
      state.notifyEvents = events.filter((event) => {
        const id = event?.event_id || event?.event?.event_id;
        return !viewedNotifyIds.includes(id);
      });
    },
    setViewedNotifyIds(state, { payload: { id } }) {
      const notifyEvents = [...state.notifyEvents];
      const viewedNotifyIds = [...state.viewedNotifyIds];
      const allIds = [...state.allIds];
      state.viewedNotifyIds = [
        ...viewedNotifyIds.filter((el) => allIds.includes(el)),
        id,
      ];
      state.notifyEvents = [
        ...notifyEvents.filter((event) => (event.event_id || event.event.event_id) !== id),
      ];
    },
    setShownNotifyIds(state, { payload: { id } }) {
      const allIds = [...state.allIds];
      const shownNotify = [...state.shownNotifyEvents];
      state.shownNotifyEvents = [
        ...shownNotify.filter((el) => allIds.includes(el)),
        id,
      ];
    },
    setAsyncData: {
      reducer(state, { payload: event }) {
        if (!notify) notify = new UIfx(`/static/media/attention.mp3?v=${Math.random()}`);
        if (!state.allIds.includes(event.event_id)) {
          state.data = [event, ...state.data].slice(0, state.pageSize);
          state.allIds = [event.event_id, ...state.allIds].slice(0, state.pageSize);
          state.notifyEvents = [event, ...state.notifyEvents].slice(0, state.pageSize);
        } else return;
        if (state.isSoundOn && state.soundThreshold
          && event?.top_match?.similarity >= Number((state.soundThreshold / 100).toFixed(4))) {
          notify.play();
        }
      },
      prepare: (rawData) => ({ payload: typeof rawData === 'string' ? JSON.parse(rawData) : rawData }),
    },
    setIsTopMatchHiding(state) {
      state.isTopMatchHiding = !state.isTopMatchHiding;
    },
  },
});

export default store.reducer;

export const {
  setAsyncData,
  setEvents,
  setParams,
  setFilterCounts,
  setIsTopMatchHiding,
  setNotifyEvents,
  setViewedNotifyIds,
  setShownNotifyIds,
} = store.actions;

export const disconnectAsyncEvents = async () => {
  await apiContainer.lunaClient.ws.disconnect();
};

export const connectAsyncEvents = (params = {}) => async (dispatch, getState) => {
  await disconnectAsyncEvents();
  const isAccountUser = selectAccountRole(getState()) === roles.USER;
  const accountSources = selectSourceOptionsByAccount(getState())
    .reduce((acc, { options }) => [...acc, ...options], [])
    .map(({ value }) => value);

  if (isAccountUser && accountSources.length === 0) {
    return;
  }
  const localParams = { ...params };
  if (isAccountUser && !localParams.sources) {
    localParams.sources = accountSources;
  }

  apiContainer.lunaClient.ws.connect(localParams, null, { startClosed: false });
  apiContainer.lunaClient.ws.onmessage = async (event) => {
    dispatch(setAsyncData(event));
  };
};

export const fetchLatestEvents = async (dispatch, getState) => {
  const isAccountUser = selectAccountRole(getState()) === roles.USER;
  const accountSources = selectSourceOptionsByAccount(getState())
    .reduce((acc, { options }) => [...acc, ...options], [])
    .map(({ value }) => value);

  const { latestEvents: { pageSize, filters } } = getState();

  if (isAccountUser && accountSources.length === 0) {
    dispatch(setEvents({ events: [] }));
    return;
  }

  const localFilters = { ...filters };

  if (isAccountUser && !localFilters.sources?.length) {
    localFilters.sources = accountSources;
  }

  try {
    const events = await apiContainer.lunaClient.events
      .getAll(getEventsQS({ ...localFilters, page: 1, page_size: pageSize }));

    dispatch(setEvents({ events }));
    dispatch(setNotifyEvents({ events }));
  } catch (error) {
    dispatch(setEvents({ events: [] }));
    dispatch(setNotifyEvents({ events: [] }));
    throw error;
  }
};

export const resetFilters = (dispatch) => {
  dispatch(setParams({ filters: {} }));
};

export const updateFilters = (params) => async (dispatch) => {
  const { filters } = params;
  const asyncFilters = getWsEventsQS(filters);

  dispatch(setParams(params));
  dispatch(fetchLatestEvents);
  dispatch(connectAsyncEvents(asyncFilters));
};
